import axios from 'axios';
import {
  DOCUMENT_AI_BATCH_BASE_URL,
  getDocumentAiBatchBaseUrl,
} from '@/pages/demo/utils/documentAiBatchJobDelete';

const API_BASE = import.meta.env.VITE_API_URL || 'https://dev.api.rudalink.com';

const ocrAxios = axios.create({
  timeout: 600_000,
  headers: { Accept: 'application/json' },
});

const publicApiAxios = axios.create({
  baseURL: API_BASE,
  timeout: 120_000,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

export const demoOcrService = {
  processUpload: async (formData: FormData, useTaskQueue = false) => {
    const baseUrl = getDocumentAiBatchBaseUrl({ useTaskQueueClient: useTaskQueue });
    const response = await ocrAxios.post(`${baseUrl}/document-ai/process-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  processUrlBatch: async (payload: { imageUrls: string[] }, useTaskQueue = false) => {
    const baseUrl = getDocumentAiBatchBaseUrl({ useTaskQueueClient: useTaskQueue });
    const response = await ocrAxios.post(`${baseUrl}/document-ai/process-url/batch`, payload);
    return response.data;
  },

  fetchBatchJobs: async () => {
    const response = await fetch(`${DOCUMENT_AI_BATCH_BASE_URL}/document-ai/batch/jobs`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const text = await response.text().catch(() => '');
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { _raw: text };
    }
    return { ok: response.ok, status: response.status, body, fetchedAt: new Date() };
  },

  fetchBatchJob: async (jobId: string) => {
    const url = `${DOCUMENT_AI_BATCH_BASE_URL}/document-ai/batch/jobs/${encodeURIComponent(jobId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const text = await response.text().catch(() => '');
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { _raw: text };
    }
    return { ok: response.ok, status: response.status, body };
  },

  requestPreprocessedOcr: async (formData: FormData) => {
    const response = await publicApiAxios.post(
      '/api/v1/max-match/mini/web/prescriptions/ocr/preprocessed',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  requestPostProcess: async (payload: unknown) => {
    const response = await publicApiAxios.post(
      '/api/v1/max-match/mini/web/prescriptions/ocr/post-process',
      payload,
    );
    return response.data;
  },
};

export const extractJobsArrayFromListResponse = (body: unknown): Record<string, unknown>[] => {
  if (body == null) return [];
  if (Array.isArray(body)) return body.filter(Boolean) as Record<string, unknown>[];
  if (typeof body !== 'object') return [];
  const obj = body as Record<string, unknown>;
  for (const key of ['jobs', 'items', 'data', 'results']) {
    const candidate = obj[key];
    if (Array.isArray(candidate)) return candidate.filter(Boolean) as Record<string, unknown>[];
  }
  return [];
};
